import React, { useState } from 'react';
import {
  Box,
  SwipeableDrawer,
  Typography,
  IconButton,
  Button,
  Stack,
  Card,
  CardMedia,
  alpha,
  Fade
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingBag as ShoppingBagIcon,
  Restaurant as RestaurantIcon
} from '@mui/icons-material';
import { useOrder } from '../context/OrderContext';
import { colors, spacing, typography, borderRadius, shadows } from '../theme/designTokens';

const MyOrderDrawer = ({ open, onClose, onOpen, restaurantName }) => {
  const orderContext = useOrder();
  const { 
    orderItems = [], 
    increaseQuantity, 
    decreaseQuantity, 
    removeItem, 
    clearOrder, 
    total = 0, 
    itemCount = 0 
  } = orderContext || {};
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleClearOrder = () => {
    clearOrder();
    setShowClearConfirm(false);
  };

  // Empty state component
  const EmptyOrderState = () => (
    <Box
      sx={{
        textAlign: 'center',
        py: spacing[8],
        px: spacing[3]
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          p: spacing[4],
          borderRadius: borderRadius.full,
          bgcolor: alpha(colors.primary[600], 0.1),
          mb: spacing[3]
        }}
      >
        <ShoppingBagIcon sx={{ fontSize: 64, color: colors.primary[600], opacity: 0.5 }} />
      </Box>
      <Typography
        variant="h6"
        fontWeight={typography.fontWeight.bold}
        sx={{
          color: colors.secondary[800],
          mb: spacing[1]
        }}
      >
        Your order is empty
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: colors.secondary[500],
          lineHeight: typography.lineHeight.relaxed,
          maxWidth: '280px',
          mx: 'auto'
        }}
      >
        Add items from the menu to get started
      </Typography>
    </Box>
  );

  // Order item component
  const OrderItemCard = ({ item }) => (
    <Card
      sx={{
        display: 'flex',
        mb: spacing[2],
        borderRadius: borderRadius.lg,
        overflow: 'hidden',
        boxShadow: shadows.sm,
        bgcolor: 'white'
      }}
    >
      {/* Item Image */}
      <Box
        sx={{
          width: 80,
          height: 80,
          flexShrink: 0,
          position: 'relative',
          bgcolor: item.image_url ? 'transparent' : alpha(colors.primary[600], 0.1)
        }}
      >
        {item.image_url ? (
          <CardMedia
            component="img"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            image={item.image_url}
            alt={item.item_name}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <RestaurantIcon sx={{ fontSize: 32, color: colors.primary[600], opacity: 0.5 }} />
          </Box>
        )}
      </Box>

      {/* Item Details */}
      <Box
        sx={{
          flex: 1,
          p: spacing[1.5],
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={typography.fontWeight.semibold}
            sx={{
              color: colors.secondary[800],
              mb: spacing[0.25],
              lineHeight: typography.lineHeight.tight
            }}
          >
            {item.item_name}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={typography.fontWeight.bold}
            sx={{
              color: colors.primary[600]
            }}
          >
            ₹{item.price}
          </Typography>
        </Box>

        {/* Quantity Controls and Delete */}
        <Stack direction="row" spacing={spacing[1]} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={spacing[0.5]} alignItems="center">
            <IconButton
              size="small"
              onClick={() => decreaseQuantity(item.item_id)}
              sx={{
                bgcolor: alpha(colors.primary[600], 0.1),
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: alpha(colors.primary[600], 0.2)
                },
                '&:active': {
                  transform: 'scale(0.95)'
                }
              }}
            >
              <RemoveIcon sx={{ fontSize: 18, color: colors.primary[600] }} />
            </IconButton>

            <Typography
              variant="body1"
              fontWeight={typography.fontWeight.bold}
              sx={{
                minWidth: 32,
                textAlign: 'center',
                color: colors.secondary[800]
              }}
            >
              {item.quantity}
            </Typography>

            <IconButton
              size="small"
              onClick={() => increaseQuantity(item.item_id)}
              sx={{
                bgcolor: alpha(colors.primary[600], 0.1),
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: alpha(colors.primary[600], 0.2)
                },
                '&:active': {
                  transform: 'scale(0.95)'
                }
              }}
            >
              <AddIcon sx={{ fontSize: 18, color: colors.primary[600] }} />
            </IconButton>
          </Stack>

          {/* Subtotal and Delete */}
          <Stack direction="row" spacing={spacing[1]} alignItems="center">
            <Typography
              variant="subtitle2"
              fontWeight={typography.fontWeight.bold}
              sx={{
                color: colors.secondary[700]
              }}
            >
              ₹{item.price * item.quantity}
            </Typography>
            <IconButton
              size="small"
              onClick={() => removeItem(item.item_id)}
              sx={{
                color: colors.error,
                '&:hover': {
                  bgcolor: alpha(colors.error, 0.1)
                },
                '&:active': {
                  transform: 'scale(0.95)'
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      disableSwipeToOpen={false}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: borderRadius['2xl'],
          borderTopRightRadius: borderRadius['2xl'],
          maxHeight: '85vh',
          height: orderItems.length === 0 ? 'auto' : '85vh'
        }
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: colors.background.secondary,
          position: 'relative'
        }}
      >
        {/* Drag Handle - Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: spacing[1],
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: borderRadius.full,
              bgcolor: colors.secondary[400],
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          />
        </Box>

        {/* Header */}
        <Box
          sx={{
            px: spacing[3],
            pt: spacing[3],
            pb: spacing[1.5],
            bgcolor: 'white',
            borderBottom: `1px solid ${colors.secondary[200]}`
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography
                variant="h6"
                fontWeight={typography.fontWeight.bold}
                sx={{ color: colors.secondary[800] }}
              >
                My Order
              </Typography>
              {restaurantName && (
                <Typography
                  variant="caption"
                  sx={{ color: colors.secondary[500] }}
                >
                  {restaurantName}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: colors.secondary[600],
                '&:hover': {
                  bgcolor: alpha(colors.secondary[600], 0.1)
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Item Count */}
          {orderItems.length > 0 && (
            <Box sx={{ mt: spacing[1.5] }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography
                  variant="body2"
                  sx={{ color: colors.secondary[600] }}
                >
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </Typography>
                {orderItems.length > 0 && !showClearConfirm && (
                  <Button
                    size="small"
                    onClick={() => setShowClearConfirm(true)}
                    sx={{
                      color: colors.error,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'none',
                      fontWeight: typography.fontWeight.semibold,
                      '&:hover': {
                        bgcolor: alpha(colors.error, 0.1)
                      }
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </Stack>

              {/* Clear Confirmation */}
              {showClearConfirm && (
                <Fade in>
                  <Box
                    sx={{
                      mt: spacing[1],
                      p: spacing[1.5],
                      bgcolor: alpha(colors.error, 0.1),
                      borderRadius: borderRadius.md,
                      border: `1px solid ${colors.error}`
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: colors.error, display: 'block', mb: spacing[1] }}
                    >
                      Clear all items from your order?
                    </Typography>
                    <Stack direction="row" spacing={spacing[1]}>
                      <Button
                        size="small"
                        onClick={handleClearOrder}
                        sx={{
                          bgcolor: colors.error,
                          color: 'white',
                          fontSize: typography.fontSize.xs,
                          px: spacing[2],
                          py: spacing[0.5],
                          textTransform: 'none',
                          fontWeight: typography.fontWeight.semibold,
                          '&:hover': {
                            bgcolor: '#dc2626'
                          }
                        }}
                      >
                        Yes, Clear
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setShowClearConfirm(false)}
                        sx={{
                          color: colors.secondary[700],
                          fontSize: typography.fontSize.xs,
                          px: spacing[2],
                          py: spacing[0.5],
                          textTransform: 'none',
                          fontWeight: typography.fontWeight.semibold,
                          '&:hover': {
                            bgcolor: alpha(colors.secondary[600], 0.1)
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Box>
                </Fade>
              )}
            </Box>
          )}
        </Box>

        {/* Order Items or Empty State */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: spacing[3],
            py: spacing[2],
            '&::-webkit-scrollbar': {
              width: 6
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: colors.secondary[300],
              borderRadius: borderRadius.full
            }
          }}
        >
          {orderItems.length === 0 ? (
            <EmptyOrderState />
          ) : (
            orderItems.map((item) => (
              <OrderItemCard key={item.item_id} item={item} />
            ))
          )}
        </Box>

        {/* Footer with Total and Action Button */}
        {orderItems.length > 0 && (
          <Box
            sx={{
              bgcolor: 'white',
              borderTop: `1px solid ${colors.secondary[200]}`,
              px: spacing[3],
              py: spacing[2.5],
              pb: `calc(${spacing[2.5]} + env(safe-area-inset-bottom))`
            }}
          >
            {/* Total */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={spacing[2]}>
              <Typography
                variant="h6"
                fontWeight={typography.fontWeight.bold}
                sx={{ color: colors.secondary[800] }}
              >
                Total
              </Typography>
              <Typography
                variant="h5"
                fontWeight={typography.fontWeight.extrabold}
                sx={{
                  color: colors.primary[600]
                }}
              >
                ₹{total}
              </Typography>
            </Stack>

            {/* Friendly Message */}
            <Box
              sx={{
                textAlign: 'center',
                py: spacing[2],
                px: spacing[2],
                bgcolor: alpha(colors.accent[600], 0.1),
                borderRadius: borderRadius.lg,
                border: `2px dashed ${alpha(colors.accent[600], 0.3)}`
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: colors.secondary[800],
                  fontWeight: typography.fontWeight.semibold,
                  mb: spacing[0.5],
                  fontSize: typography.fontSize.base
                }}
              >
                Ready to order? 🍽️
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.secondary[600],
                  lineHeight: typography.lineHeight.relaxed
                }}
              >
                Please call a waiter to place your order. Your delicious meal will be ready in 15-20 minutes!
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </SwipeableDrawer>
  );
};

export default MyOrderDrawer;
